import { accountCreated, signupSubmitted } from './events';

/**
 * DIT-513 — what the funnel's own responses turn into, as event parameters.
 *
 * Separate from the pages for the reason every rule in this codebase ends up
 * separate from its component: a test bound to a page needs a render harness
 * this repo does not have, and a test that copies the mapping beside itself
 * passes whatever the mapping later becomes. These are the real functions the
 * pages call.
 *
 * **Neither can throw.** Both are called from the step they report — a signup the
 * server accepted, an account it created — and an error escaping from measurement
 * would show that step as failed and stop it where it stands.
 */

function neverThrows(report: () => boolean): boolean {
  try {
    return report();
  } catch {
    return false;
  }
}

/**
 * Report an accepted signup form. The same address submitted again from this
 * browser is reported once — see `signupSubmitted`.
 *
 * **A resubmitted registration reports nothing.** The server answers a submit of a
 * registration whose link is still unused with `resubmitted: true`. A visitor who
 * has not consented is remembered on this page only, so a reload was enough to
 * count one signup twice in the number campaigns bid on; the server needs no
 * storage to know.
 */
export function reportSignupSubmitted(email: string, resubmitted = false): boolean {
  return neverThrows(() => (resubmitted ? false : signupSubmitted(email)));
}

/** The half of `/verify/`'s response the event reads. All optional: a landing
 *  deployed ahead of the API sees none of it. */
export interface VerifiedRegistration {
  company_id?: string;
  plan?: string;
  /** Present only when the funnel chose a seat count. */
  seats?: number;
  /** DIT-517 (2026-09-03): the ONE field manual Advanced Matching may send,
   *  computed server-side. Absence means the parameter is left out — never
   *  hashed here as a fallback. */
  email_sha256?: string;
  /** The link was opened again after the account was created — often in another
   *  browser, where this browser's once-only key cannot see the first report. */
  resumed?: boolean;
}

/**
 * Report a confirmed account. Returns whether it was delivered now.
 *
 * Every field comes from the response and none from the funnel store, which is
 * empty whenever the verification link is opened on another device.
 *
 * **A resumed response reports nothing.** The server answers a second click on
 * an already-verified link with `resumed: true`. De-duplication in this browser
 * cannot catch it when the first click was on another device — sign up on the
 * desktop, open the link on the phone, click the same e-mail on the desktop later
 * — so the server's word is what decides.
 */
export function reportAccountCreated(result: VerifiedRegistration): boolean {
  return neverThrows(() => {
    if (result.resumed) return false;
    return accountCreated({
      companyId: result.company_id ?? '',
      planId: result.plan,
      seats: result.seats,
      emailSha256: result.email_sha256,
    });
  });
}
